/*eslint react/no-string-refs: "off" */
import { FC, useState } from 'react';
import { display } from 'components/ErrorView/ErrorDialog';
import { updateItemThumbnail } from 'store/providers/providerSlice';
import { IProviderItemView } from '../../../../store/providers/providerTypes';
import BlurModal from './BlurModal';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { videoFormat } from 'utils/check-file-type/listOfFilesFormat';
import VideoThumbnailPicker from './VideoThumbnailPicker';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import ImageCropper from './ImageCropper';
import ItemPreviewer, { TDisplayMode } from './ItemPreviewer';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import noImage from '../../../../assets/images/noImage.png';

const BlurModalContainer = styled.div`
  max-height: 70vh;
  overflow: auto;
`;

const ChangeStatusContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: row-reverse;
`;

const ItemPreviewerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem auto;
`;

interface IComponentProps {
  isOpen: boolean;
  item: IProviderItemView;
  dispatch: (action: any) => any;
  onCloseThumbnailModal: (status: boolean) => void;
}

const ChangeThumbnailModal: FC<IComponentProps> = ({
  isOpen,
  item,
  onCloseThumbnailModal,
  dispatch,
}) => {
  const { t } = useTranslation('documentManager');

  const parentItem = useSelector(
    (state: RootState) =>
      state.providers.items[item.providerId]?.items?.[item.dadId]?.data
  );

  const buttonGroupItems: Record<string, string> = {
    picture: t('choosePicture'),
  };

  if (videoFormat.includes(item.document?.properties.format)) {
    buttonGroupItems['video'] = t('chooseVideo');
  }

  const [selectedBtn, setSelectedBtn] = useState<string>('picture');
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [picture, setPicture] = useState<any>(undefined);
  const [preview, setPreview] = useState<string>(item.thumbnailUrl);

  const handleChangeThumbnail = (blob: any) => {
    setPicture(blob);
  };

  const handleSaveThumbnail = () => {
    setSaving(true);
    dispatch(
      updateItemThumbnail({
        item: item,
        blob: picture,
      })
    ).then(
      () => {
        setSaving(false);
        onCloseThumbnailModal(true);
      },
      (err: any) => {
        setSaving(false);
        display(err);
      }
    );
  };

  const handleRemoveThumbnail = () => {
    setDeleting(true);
    dispatch(
      updateItemThumbnail({
        item: item,
        blob: null,
      })
    ).then(
      () => {
        setDeleting(false);
        onCloseThumbnailModal(true);
      },
      (err: any) => {
        setDeleting(false);
        display(err);
      }
    );
  };

  const handleSetPreviewImage = (preview: string) => {
    setPreview(preview);
  };

  const currentItemViewTypeList = item?.viewType?.split(',');
  const parentItemViewTypeList = parentItem?.viewType?.split(',');
  const displayMode = (parentItemViewTypeList?.find((el: string) =>
    el.startsWith('display_')
  ) || 'display_sm') as TDisplayMode;
  const cellType = currentItemViewTypeList?.includes('card_lg') ? 'lg' : 'md';

  return (
    <BlurModal
      isOpen={isOpen}
      onCloseModal={() => onCloseThumbnailModal(false)}
      title={`${t('changeThumbnail')} ${item.name}`}
      maxWidth="80vw"
    >
      <BlurModalContainer>
        <ItemPreviewerContainer>
          <ItemPreviewer
            item={item}
            displayMode={displayMode}
            cellType={cellType}
            thumbnailUrl={preview || item.thumbnailUrl || noImage}
          />
        </ItemPreviewerContainer>
        {Object.values(buttonGroupItems).length > 1 && (
          <ButtonGroup
            items={buttonGroupItems}
            selected={selectedBtn}
            onSelect={(val) => setSelectedBtn(val)}
          />
        )}

        {selectedBtn === 'video' ? (
          <VideoThumbnailPicker
            videoUrl={item.document?.url}
            onThumbnailSelected={handleChangeThumbnail}
            previewImage={preview}
            onSetPreview={handleSetPreviewImage}
          />
        ) : (
          <ImageCropper
            aspectRatio={1}
            onCropped={handleChangeThumbnail}
            onSetPreview={handleSetPreviewImage}
          />
        )}

        <ChangeStatusContainer>
          {!(picture === undefined) ? (
            <Button
              onClick={handleSaveThumbnail}
              color="green"
              loading={saving}
            >
              {t('save')}
            </Button>
          ) : null}
          <Button
            onClick={(e) => {
              e.preventDefault();
              onCloseThumbnailModal(false);
            }}
            color="red"
            disabled={saving || deleting}
          >
            {t('cancel')}
          </Button>
          {item?.thumbnailUrl && (
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleRemoveThumbnail();
              }}
              color="red"
              disabled={deleting || saving}
            >
              {t('deleteCurrentThumbnail')}
            </Button>
          )}
        </ChangeStatusContainer>
      </BlurModalContainer>
    </BlurModal>
  );
};

export default ChangeThumbnailModal;
